import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Wrap = styled.div`
  padding: 80px 24px 0;
  max-width: 1100px;
  margin: 0 auto;
`;

const Head = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const Kicker = styled.div`
  font-size: 0.8rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--accent-2);
  font-weight: 600;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.2rem, 5vw, 3.4rem);
  margin: 0 0 18px;
  letter-spacing: -0.02em;
`;

const Lead = styled.p`
  max-width: 680px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--text-dim);
`;

const Pillars = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 80px;
  @media (max-width: 880px) { grid-template-columns: 1fr; }
`;

const Pillar = styled(motion.div)`
  padding: 32px;
  border-radius: 20px;
  background: var(--surface);
  border: 1px solid var(--border);
`;

const PIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $accent }) => $accent};
  display: grid;
  place-items: center;
  color: #fff;
  margin-bottom: 18px;
`;

const PTitle = styled.h3`
  margin: 0 0 8px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
`;

const PDesc = styled.p`
  margin: 0;
  color: var(--text-dim);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 40px;
  margin-bottom: 80px;
  @media (max-width: 880px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  padding: 36px;
  border-radius: 24px;
  background: var(--surface);
  border: 1px solid var(--border);
`;

const CardHead = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  margin: 0 0 20px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const InfoIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: var(--surface-strong);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--accent-2);
`;

const InfoBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.95rem;
  strong {
    font-size: 0.78rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-mute);
    font-weight: 600;
  }
  a, span {
    color: var(--text);
    transition: color 0.2s ease;
  }
  a:hover { color: var(--accent-2); }
`;

const Hours = styled.div`
  margin-top: 24px;
  padding: 18px;
  border-radius: 14px;
  background: rgba(124, 92, 255, 0.08);
  border: 1px solid rgba(124, 92, 255, 0.2);
  font-size: 0.92rem;
  color: var(--text-dim);
  strong {
    display: block;
    color: var(--accent-3);
    font-size: 0.78rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 8px;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const Field = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(7, 6, 15, 0.5);
  border: 1px solid var(--border);
  color: var(--text);
  font-family: inherit;
  font-size: 1rem;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:focus {
    outline: none;
    border-color: var(--accent-2);
    background: rgba(7, 6, 15, 0.75);
  }
  &::placeholder { color: var(--text-mute); }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(7, 6, 15, 0.5);
  border: 1px solid var(--border);
  color: var(--text);
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:focus {
    outline: none;
    border-color: var(--accent-2);
    background: rgba(7, 6, 15, 0.75);
  }
  &::placeholder { color: var(--text-mute); }
`;

const Submit = styled.button`
  margin-top: 8px;
  padding: 16px 28px;
  border-radius: 14px;
  background: var(--grad-warm);
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: var(--shadow-glow);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
  &:hover { transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Sent = styled.div`
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(68, 221, 136, 0.1);
  border: 1px solid rgba(68, 221, 136, 0.35);
  color: #8fe9b3;
  font-size: 0.95rem;
`;

function Icon({ d }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

export default function About() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setBusy(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const subject = encodeURIComponent(`[Zero Immersive] ${data.get('subject') || 'Website inquiry'}`);
    const body = encodeURIComponent(
      `Name: ${data.get('name')}\nEmail: ${data.get('email')}\n\n${data.get('message')}`
    );
    window.location.href = `mailto:accounts@zeroimmersive.com?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setBusy(false);
      setSent(true);
      form.reset();
    }, 400);
  };

  return (
    <Wrap>
      <Head>
        <Kicker>About the studio</Kicker>
        <Title>Built by people who actually finish their phones' batteries playing games.</Title>
        <Lead>
          Zero Immersive Inc is a Canadian mobile game studio based in
          Mississauga, Ontario. We design small, sharp games that earn their
          place on your home screen — and we're always one update away from the
          next idea.
        </Lead>
      </Head>

      <Pillars>
        <Pillar
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <PIcon $accent="linear-gradient(135deg, #ff7a3d, #ff3d7f)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2" fill="currentColor"/></svg>
          </PIcon>
          <PTitle>Our mission</PTitle>
          <PDesc>Make mobile games people genuinely look forward to opening — not just another notification.</PDesc>
        </Pillar>
        <Pillar
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.08 }}
        >
          <PIcon $accent="linear-gradient(135deg, #7c5cff, #3dd6ff)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </PIcon>
          <PTitle>Our team</PTitle>
          <PDesc>A small, focused crew of designers, engineers, and artists — each one shipping work they'd be proud to play themselves.</PDesc>
        </Pillar>
        <Pillar
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.16 }}
        >
          <PIcon $accent="linear-gradient(135deg, #3dd6ff, #7c5cff)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M9 13.5L7.5 22l4.5-3 4.5 3L15 13.5"/></svg>
          </PIcon>
          <PTitle>Our values</PTitle>
          <PDesc>Polish over scope. Honest design over dark patterns. Ship something we'd happily install on our own phones.</PDesc>
        </Pillar>
      </Pillars>

      <Split>
        <Card>
          <CardHead>Reach the studio</CardHead>
          <InfoRow>
            <InfoIcon><Icon d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /></InfoIcon>
            <InfoBody>
              <strong>Location</strong>
              <span>Mississauga, Ontario, Canada</span>
            </InfoBody>
          </InfoRow>
          <InfoRow>
            <InfoIcon><Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" /></InfoIcon>
            <InfoBody>
              <strong>Email</strong>
              <a href="mailto:accounts@zeroimmersive.com">accounts@zeroimmersive.com</a>
            </InfoBody>
          </InfoRow>
          <InfoRow>
            <InfoIcon><Icon d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></InfoIcon>
            <InfoBody>
              <strong>Phone</strong>
              <a href="tel:+16475104511">+1 (647) 510-4511</a>
            </InfoBody>
          </InfoRow>
          <Hours>
            <strong>Studio hours</strong>
            Monday – Friday · 9:00 AM – 6:00 PM EST<br/>
            Weekends · Closed (we're playing)
          </Hours>
        </Card>

        <Card>
          <CardHead>Send us a message</CardHead>
          {sent ? (
            <Sent>
              Thanks — your email client should have opened with your message
              ready to send. If it didn't, write us directly at
              accounts@zeroimmersive.com.
            </Sent>
          ) : (
            <Form onSubmit={onSubmit}>
              <Field><Input name="name" required placeholder="Your name" /></Field>
              <Field><Input type="email" name="email" required placeholder="Your email" /></Field>
              <Field><Input name="subject" placeholder="Subject (optional)" /></Field>
              <Field><Textarea name="message" required placeholder="What's on your mind?" /></Field>
              <Submit type="submit" disabled={busy}>
                {busy ? 'Opening your email…' : 'Send message'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </Submit>
            </Form>
          )}
        </Card>
      </Split>
    </Wrap>
  );
}
